import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { useAuth } from '@/contexts/AuthProvider';

export type RoleType = 'organizador' | 'maestro' | 'academia' | 'marca';

function debugRoleRequests(...args: unknown[]) {
  if (import.meta.env.DEV) {
    console.log(...args);
  }
}

export type RoleRequest = {
  id: number;
  user_id: string;
  role: RoleType;
  note?: string | null;
  status: 'pendiente' | 'aprobado' | 'rechazado';
  created_at: string;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
};

export function useMyRoleRequests() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["role-requests", user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<RoleRequest[]> => {
      const { data, error } = await supabase
        .from("role_requests")
        .select("*")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as RoleRequest[];
    },
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });
}

export function useRequestRole() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ role, note }: { role: RoleType; note?: string }) => {
      debugRoleRequests('[useRequestRole] Requesting role:', { role, note });
      const { error } = await supabase
        .from("role_requests")
        .insert({ user_id: user!.id, role, note: note || null });
      if (error) {
        console.error('[useRequestRole] Error:', error);
        throw error;
      }
      debugRoleRequests('[useRequestRole] Success');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["role-requests", user?.id] });
    }
  });
}

/** Admin hooks */
export function useAdminRoleRequests(status?: 'pendiente' | 'aprobado' | 'rechazado') {
  return useQuery({
    queryKey: ["admin-role-requests", status],
    queryFn: async (): Promise<RoleRequest[]> => {
      debugRoleRequests('[useAdminRoleRequests] Fetching requests with status:', status);
      let q = supabase
        .from("role_requests")
        .select("*")
        .order("created_at", { ascending: true });
      
      if (status) q = q.eq("status", status);
      
      const { data, error } = await q;
      if (error) {
        console.error('[useAdminRoleRequests] Error:', error);
        throw error;
      }
      debugRoleRequests('[useAdminRoleRequests] Success:', data?.length, 'requests');
      return data as RoleRequest[];
    },
    refetchInterval: status === 'pendiente' ? 15_000 : false,
    refetchOnWindowFocus: true,
  });
}

export function useApproveRoleRequest() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, approve, note }: { id: number; approve: boolean; note?: string }) => {
      debugRoleRequests('[useApproveRoleRequest] Approving request:', { id, approve, note });
      
      // Obtener el user_id y role_slug de la solicitud antes de aprobarla
      const { data: requestData, error: fetchError } = await supabase
        .from("role_requests")
        .select("user_id, role_slug, role")
        .eq("id", id)
        .single();
      
      if (fetchError) {
        console.warn('[useApproveRoleRequest] Error fetching request data:', fetchError);
      }
      
      const { error } = await supabase.rpc("approve_role_request", {
        p_request_id: id,
        p_approve: approve,
        p_note: note || null
      });
      if (error) {
        console.error('[useApproveRoleRequest] Error:', error);
        throw error;
      }
      debugRoleRequests('[useApproveRoleRequest] Success');
      
      return { requestData: requestData || null, approved: approve };
    },
    onSuccess: async (data) => {
      const { requestData, approved } = data;
      
      // Invalidar queries de admin
      qc.invalidateQueries({ queryKey: ["admin-role-requests"] });
      // Invalidar queries de roles aprobados (para que el switch se actualice)
      qc.invalidateQueries({ queryKey: ["my-approved-roles"] });
      // Invalidar queries de role requests del usuario
      qc.invalidateQueries({ queryKey: ["role-requests"] });
      qc.invalidateQueries({ queryKey: ["role-requests-me"] });
      qc.invalidateQueries({ queryKey: ["role_requests_me"] });
      qc.invalidateQueries({ queryKey: ["user_roles"] });
      
      // Si se aprobó, invalidar también las queries de los perfiles específicos
      if (approved && requestData) {
        const userId = requestData.user_id;
        const roleSlug = requestData.role_slug || (requestData as any).role;
        
        // Invalidar queries de perfiles según el rol aprobado
        switch (roleSlug) {
          case 'organizador':
            qc.invalidateQueries({ queryKey: ["organizer", "me", userId] });
            qc.invalidateQueries({ queryKey: ["organizer"] });
            break;
          case 'academia':
            qc.invalidateQueries({ queryKey: ["academy", "mine", userId] });
            qc.invalidateQueries({ queryKey: ["academy"] });
            break;
          case 'maestro':
            qc.invalidateQueries({ queryKey: ["teacher", "mine", userId] });
            qc.invalidateQueries({ queryKey: ["teacher"] });
            break;
          case 'marca':
            qc.invalidateQueries({ queryKey: ["brand", "mine"] });
            qc.invalidateQueries({ queryKey: ["brand"] });
            break;
        }
        
        // Invalidar también las queries del usuario aprobado (si es diferente del admin)
        if (userId && userId !== user?.id) {
          qc.invalidateQueries({ queryKey: ["my-approved-roles", userId] });
        }
      }
    }
  });
}

export function useIsAdmin() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["is-admin", user?.id],
    enabled: !!user?.id,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase
        .from("admins")
        .select("user_id")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return !!data;
    }
  });
}

